// src/components/Dashboard/CreditReportSummary.js

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Alert,
  Badge,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";

const currencySymbol = process.env.REACT_APP_CURRENCY_SYMBOL || "R";
// Ensure NaN becomes 0.00
const formatCurrency = (amt) => {
  const num = parseFloat(amt);
  const valid = isNaN(num) ? 0 : num;
  return `${currencySymbol}${valid.toFixed(2)}`;
};

// Wrap gauge label into at most two lines
const wrapGaugeLabel = (label) => {
  const words = label.split(" ");
  if (words.length <= 2) {
    return words.join("<br/>");
  }
  return `${words[0]}<br/>${words.slice(1).join(" ")}`;
};

const SECTION_ORDER = [
  "Personal Information",
  "Previous Enquiries",
  "Accounts in Arrears",
  "Open Accounts",
  "Closed Accounts",
  "Adverse Accounts",
  "Collection Accounts",
  "Debt Review",
  "Dispute & Instalment",
  "Judgements",
];

const COLUMN_CONFIG = {
  "Open Accounts": ["account_No", "supplier_Name", "current_Balance"],
  "Closed Accounts": ["account_No", "supplier_Name", "current_Balance"],
  "Accounts in Arrears": [
    "account_No",
    "supplier_Name",
    "current_Balance",
    "amount_Overdue",
  ],
  "Adverse Accounts": ["account_No", "supplier_Name", "current_Balance"],
  "Collection Accounts": ["account_No", "supplier_Name", "current_Balance"],
  Judgements: ["account_No", "supplier_Name", "current_Balance"],
};

const ALWAYS_DISPLAY_SECTIONS = [
  "Personal Information",
  "Account Data Summary",
];

const humanize = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\bPerc\b/, "Percentage")
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export default function CreditReportSummary({ data = {}, summary = {} }) {
  const [open, setOpen] = useState({ "Account Data Summary": true });
  const toggle = (sec) => setOpen((prev) => ({ ...prev, [sec]: !prev[sec] }));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const zoomConfig = {
    xs: process.env.REACT_APP_MOBILE_ZOOM_FACTOR || 0.8,
    md: process.env.REACT_APP_DESKTOP_ZOOM_FACTOR || 1,
  };

  // define mobile “glass” style
  const mobileGlass = {
    backgroundColor: "rgba(255,255,255,0.01)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,0.31)",
    color: "#fff",
    textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
  };

  const alertGlass = isMobile
    ? {
        backgroundColor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
        "& .MuiAlert-icon": {
          color: "#fff",
        },
      }
    : {};

  // Personal Info
  const ci = data.crContactDataResponse?.[0] || {};
  const personalInfo = {
    "ID Number": ci.id_number,
    "Date of Birth": ci.birth_Date,
    Surname: ci.surname,
    Initials: ci.initials,
    Names: ci.names,
    "Marital Status": ci.marital_Status,
    Status: ci.death_Status,
    "Address Line 1": ci.address_1_Line_1,
    Town: ci.address_1_Town,
    "Postal Code": ci.address_1_Postal_Code,
    "Home Phone": ci.h_Phone_1,
    "Work Phone": ci.w_Phone_1,
    Cellphone: ci.c_Phone_1,
    Email: ci.email_1,
    Gender: ci.iD_Gender,
    Age: ci.iD_Age,
    "Last Verified": ci.last_Verify,
  };

  // Account Data Summary
  const acct = summary.crAccountDataSummary?.[0] || summary;
  const accountDataSummary = {
    Disputes: acct.dispute_Count,
    "Dispute Instalment": formatCurrency(acct.dispute_Instalment),
    "Dispute Percentage": `${acct.dispute_Perc}%`,
    "Accounts in Arrears": acct.acc_In_Arrears_Count,
    "Arrears Instalment": formatCurrency(acct.acc_In_Arrears_Instalment),
    "Arrears Percentage": `${acct.acc_In_Arrears_Perc}%`,
    "Credit Accounts": acct.credit_Acc_Count,
    "Credit Instalment": formatCurrency(acct.credit_Acc_Instalment),
    "Credit Percentage": `${acct.credit_Acc_Perc}%`,
    "Non-Credit Accounts": acct.non_Credit_Acc_Count,
    "Non-Credit Instalment": formatCurrency(acct.non_Credit_Instalment),
    "Non-Credit Percentage": `${acct.non_Credit_Acc_Perc}%`,
    "Other Credit Accounts": acct.other_Credit_Acc_Count,
    "Other Credit Instalment": formatCurrency(acct.other_Credit_Instalment),
    "Other Credit Percentage": `${acct.other_Credit_Acc_Perc}%`,
    "Closed Accounts": acct.closed_Acc_Count,
    "Closed Instalment": formatCurrency(acct.closed_Acc_Instalment),
    "Closed Percentage": `${acct.closed_Acc_Perc}%`,
    "Credit Usage (%)": acct.credit_Usage,
  };

  // Debt Review
  const drv = data.crDrv || {};
  const debtReview = {
    "Counsellor Reg. No": drv.counsellor_Registration_Number,
    "Counsellor Name": `${drv.counsellor_First_Name} ${drv.counsellor_Last_Name}`,
    "Contact Number": drv.phone_Number,
    "Status Date": drv.status_Date,
    "Review Code": drv.debt_Review_Status_Code,
    "Review Status": drv.debt_Review_Status,
    "In Debt Review": drv.in_Debt_Review,
    Address: drv.address_Line_1,
    Town: drv.town,
    "Postal Code": drv.address_Line_Postal_Code,
    "Postal Address": drv.postal_Address_Line_1,
    "Review Email": drv.email_Address,
    "Consumer No": drv.unique_Consumer_No,
  };

  // Previous Enquiries
  const previousEnquiries = data.crPrevEnq || [];

  // Dispute & Instalment
  const disputeInstalment = {
    Dispute: data.crIsDispute?.[0]?.isDispute ? "Yes" : "No",
    "Instalment Amount": data.crInstalmentAmount?.[0]?.instalmentAmount
      ? formatCurrency(data.crInstalmentAmount[0].instalmentAmount)
      : "-",
  };

  // Account arrays
  const accts = data.creditReportAccounts || [];
  const sectionsArrays = {
    "Open Accounts": accts.filter((a) => a.acc_Status === "Open"),
    "Closed Accounts": accts.filter((a) => a.acc_Status === "Closed"),
    "Accounts in Arrears": accts.filter((a) => a.amount_Overdue > 0),
    "Adverse Accounts": data.crAdverse || [],
    "Collection Accounts": data.crCollect || [],
    Judgements: data.crJudg || [],
  };

  // Arrears grid helpers
  const renderLegend = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
      {[
        { color: "#7AC38F", label: "0 months in arrears" },
        { color: "#FFC107", label: "1 month in arrears" },
        { color: "#FF5722", label: "2 months in arrears" },
        { color: "#D32F2F", label: "3+ months in arrears" },
        { color: "#E0E0E0", label: "No data available" },
      ].map((it, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", mr: 2 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: it.color,
              mr: 0.5,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="body2">{it.label}</Typography>
        </Box>
      ))}
    </Box>
  );
  const generate12MonthData = (profile) => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      const rec = profile.find(
        (p) =>
          p.month === String(d.getMonth() + 1) &&
          p.year === String(d.getFullYear())
      );
      const raw = rec ? parseInt(rec.payment_History, 10) : null;
      return {
        month: d.toLocaleString("default", { month: "short" }),
        year: d.getFullYear().toString().slice(-2),
        missed: raw != null ? Math.max(raw, 0) : null,
      };
    });
  };
  const renderGrid = (profile) => (
    <Box sx={{ width: "100%", overflowX: "auto", mb: 3 }}>
      {renderLegend()}
      <Box
        sx={{
          display: "grid",
          gap: 0.5,
          gridTemplateColumns: {
            xs: "repeat(4, 1fr)",
            sm: "repeat(4, 1fr)",
          },
        }}
      >
        {generate12MonthData(profile).map((d, i) => {
          let bg = "#E0E0E0";
          if (d.missed === 0) bg = "#7AC38F";
          else if (d.missed === 1) bg = "#FFC107";
          else if (d.missed === 2) bg = "#FF5722";
          else if (d.missed >= 3) bg = "#D32F2F";
          return (
            <Box
              key={i}
              sx={{
                backgroundColor: bg,
                color: "#fff",
                textAlign: "center",
                py: 0.5,
                borderRadius: 0.5,
                fontSize: { xs: "0.6rem", sm: "0.8rem" },
              }}
            >
              {d.month}'{d.year}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const sections = {
    "Personal Information": { data: personalInfo, isArray: false },
    "Account Data Summary": { data: accountDataSummary, isArray: false },
    "Debt Review": { data: debtReview, isArray: false },
    "Previous Enquiries": { data: previousEnquiries, isArray: true },
    "Dispute & Instalment": { data: disputeInstalment, isArray: false },
    ...Object.fromEntries(
      Object.entries(sectionsArrays).map(([k, v]) => [
        k,
        { data: v, isArray: true },
      ])
    ),
  };

  const filteredSections = SECTION_ORDER.filter((sec) => {
    const section = sections[sec];
    if (!section) return false;
    const { data: secData, isArray } = section;
    if (ALWAYS_DISPLAY_SECTIONS.includes(sec)) return true;
    if (isArray) return secData.length > 0;
    return Object.values(secData).some((v) => v != null && v !== "-");
  });

  const renderAccountDataSummary = () => {
    const ads = sections["Account Data Summary"].data;
    const percentEntries = Object.entries(ads).filter(([k]) =>
      k.includes("Percentage")
    );
    const nonPercentEntries = Object.entries(ads).filter(
      ([k]) => !k.includes("Percentage")
    );

    const GAUGE_COLOR_MAP = {
      "Dispute Percentage": theme.palette.error.main,
      "Arrears Percentage": theme.palette.warning.main,
      "Credit Percentage": theme.palette.success.main,
      "Non-Credit Percentage": theme.palette.info.main,
      "Other Credit Percentage": theme.palette.secondary.main,
      "Closed Percentage": theme.palette.primary.main,
    };

    return (
      <Box sx={{ mb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            ...(isMobile ? mobileGlass : {}),
            "& .MuiPaper-root": { backgroundColor: "transparent" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1,
              bgcolor: isMobile ? "transparent" : "grey.50",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                ...(isMobile
                  ? { color: "#fff", textShadow: "0 1px 2px rgba(0,0,0,0.5)" }
                  : {}),
              }}
            >
              Account Data Summary
            </Typography>
          </Box>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
              {percentEntries.map(([k, v]) => {
                const raw = parseFloat(v);
                const value = isNaN(raw) ? 0 : Math.min(Math.max(raw, 0), 100);
                const progressValue = value === 0 ? 100 : value;
                const color =
                  value > 0
                    ? GAUGE_COLOR_MAP[k] || theme.palette.primary.main
                    : theme.palette.grey[300];
                return (
                  <Box
                    key={k}
                    sx={{
                      textAlign: "center",
                      flex: "1 1 calc(33.333% - 16px)",
                      maxWidth: "calc(33.333% - 16px)",
                    }}
                  >
                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                      <CircularProgress
                        variant="determinate"
                        value={progressValue}
                        size={40}
                        thickness={4}
                        sx={{ color }}
                      />
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          ...(isMobile
                            ? {
                                color: "#fff",
                                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                              }
                            : {}),
                        }}
                      >
                        {`${value}%`}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        display: "block",
                        ...(isMobile
                          ? {
                              color: "#fff",
                              textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                            }
                          : {}),
                      }}
                      dangerouslySetInnerHTML={{ __html: wrapGaugeLabel(k) }}
                    />
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ width: "100%", overflowX: "auto", zoom: zoomConfig }}>
              <Table size="small">
                <TableBody>
                  {nonPercentEntries.map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          ...(isMobile
                            ? {
                                color: "#fff",
                                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                              }
                            : {}),
                        }}
                        width="50%"
                      >
                        {k}
                      </TableCell>
                      <TableCell
                        sx={
                          isMobile
                            ? {
                                color: "#fff",
                                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                              }
                            : {}
                        }
                      >
                        {v ?? "0.00"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderSectionBody = (secKey) => {
    const { data: secData, isArray } = sections[secKey];

    if (!isArray) {
      return (
        <Box sx={{ width: "100%", overflowX: "auto", zoom: zoomConfig }}>
          <Table size="small">
            <TableBody>
              {Object.entries(secData).map(([k, v]) => (
                <TableRow key={k}>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      ...(isMobile
                        ? {
                            color: "#fff",
                            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                          }
                        : {}),
                    }}
                  >
                    {k}
                  </TableCell>
                  <TableCell
                    sx={
                      isMobile
                        ? {
                            color: "#fff",
                            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                          }
                        : {}
                    }
                  >
                    {v ?? "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      );
    }

    if (!secData.length) {
      return (
        <Typography align="center" sx={isMobile ? { color: "#fff" } : {}}>
          No data to display
        </Typography>
      );
    }

    if (secKey === "Accounts in Arrears") {
      if (isMobile) {
        return (
          <Box sx={{ zoom: zoomConfig, mb: 2 }}>
            {secData.map((row, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: 1,
                  mb: 2,
                  bgcolor: isMobile ? "transparent" : "grey.50",
                  ...(isMobile ? mobileGlass : {}),
                }}
              >
                {renderGrid(row.paymentProfile || [])}
                {COLUMN_CONFIG[secKey].map((col) => (
                  <Box
                    key={col}
                    sx={{
                      display: "flex",
                      mb: 0.5,
                      "&:last-of-type": { mb: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        fontWeight: 700,
                        width: "50%",
                        ...(isMobile
                          ? {
                              color: "#fff",
                              textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                            }
                          : {}),
                      }}
                    >
                      {humanize(col)}
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 1,
                        width: "50%",
                        ...(isMobile
                          ? {
                              color: "#fff",
                              textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                            }
                          : {}),
                      }}
                    >
                      {["current_Balance", "amount_Overdue"].includes(col)
                        ? formatCurrency(row[col])
                        : (row[col] ?? "-")}
                    </Box>
                  </Box>
                ))}
              </Paper>
            ))}
          </Box>
        );
      }
      return secData.map((row, i) => (
        <Box key={i} sx={{ mb: 2, zoom: zoomConfig }}>
          {renderGrid(row.paymentProfile || [])}
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {COLUMN_CONFIG[secKey].map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 700 }}>
                      {humanize(col)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {COLUMN_CONFIG[secKey].map((col) => (
                    <TableCell key={col}>
                      {["current_Balance", "amount_Overdue"].includes(col)
                        ? formatCurrency(row[col])
                        : (row[col] ?? "-")}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </Box>
      ));
    }

    const cols = COLUMN_CONFIG[secKey] || Object.keys(secData[0]);
    if (isMobile) {
      return (
        <Box sx={{ zoom: zoomConfig }}>
          {secData.map((row, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                p: 1,
                mb: 1,
                bgcolor: isMobile ? "transparent" : "grey.50",
                ...(isMobile ? mobileGlass : {}),
              }}
            >
              {cols.map((col) => (
                <Box
                  key={col}
                  sx={{
                    display: "flex",
                    mb: 0.5,
                    "&:last-of-type": { mb: 0 },
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: 700,
                      width: "50%",
                      ...(isMobile
                        ? {
                            color: "#fff",
                            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                          }
                        : {}),
                    }}
                  >
                    {humanize(col)}
                  </Box>
                  <Box
                    sx={{
                      width: "50%",
                      ...(isMobile
                        ? {
                            color: "#fff",
                            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                          }
                        : {}),
                    }}
                  >
                    {col.includes("Balance") || col.includes("Overdue")
                      ? formatCurrency(row[col])
                      : (row[col] ?? "-")}
                  </Box>
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      );
    }
    return (
      <Box sx={{ width: "100%", overflowX: "auto", zoom: zoomConfig }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {cols.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 700 }}>
                  {humanize(col)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {secData.map((row, i) => (
              <TableRow key={i}>
                {cols.map((col) => (
                  <TableCell key={col}>
                    {col.includes("Balance") || col.includes("Overdue")
                      ? formatCurrency(row[col])
                      : (row[col] ?? "-")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{mb: 2, ...alertGlass}}>
        This is a summary of your credit report. You can expand each section to
        view more details. If you need to download your full credit report,
        please go to the Report History section.
      </Alert>

      {/* Always-expanded ADS */}
      {renderAccountDataSummary()}

      {/* Remaining sections */}
      {filteredSections.map((sec) => {
        if (sec === "Account Data Summary") return null;
        const { data: secData, isArray } = sections[sec];
        const count = isArray ? secData.length : 0;
        return (
          <Box key={sec} sx={{ mb: 2 }}>
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                ...(isMobile ? mobileGlass : {}),
              }}
            >
              <Box
                onClick={() => toggle(sec)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  bgcolor: isMobile ? "transparent" : "grey.50",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "bold",
                    ...(isMobile
                      ? {
                          color: "#fff",
                          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        }
                      : {}),
                  }}
                >
                  {sec}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {isArray && count > 0 && (
                  <Badge
                    badgeContent={count}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        color: "#fff",
                        mr: 1,
                      },
                    }}
                  />
                )}
                <IconButton size="small" sx={isMobile ? { color: "#fff" } : {}}>
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
              <Collapse in={open[sec]} timeout="auto" unmountOnExit>
                <Box sx={{ p: 2 }}>{renderSectionBody(sec)}</Box>
              </Collapse>
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
}
